import { SyncOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message, notification } from 'antd';
import Button from 'components/Button';
import { ArgsProps } from 'antd/es/notification';
import { API } from 'network/api';
import { useNavigate } from 'react-router-dom';
import {
  DataVerificationLog,
  Stakeholder,
  StakeholderType,
  VerificationTrigger,
} from 'types/common';
import { handleAxiosError } from 'utils/helpers';
import i18n from 'utils/i18n';

const { t } = i18n.i18n;

type VerifyStakeholderPayload = {
  stakeholderId: string;
  stakeholderType?: StakeholderType;
  verificationTrigger?: VerificationTrigger;
};

type VerificationConfig = {
  showVerificationNotification?: boolean;
};

const useMutateVerifyOpenAI = (config?: VerificationConfig) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: (data: VerifyStakeholderPayload) => {
      message.loading(
        {
          key: 'verify-stakeholder',
          content:
            t('stakeholderVerification.verifyingStakeholderData') + '...',
        },
        0,
      );
      return API.post<Stakeholder>('/stakeholder/verify-with-openai', data);
    },

    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['stakeholder-data'],
      });
      const latestVerificationLog = response.data?.dataVerificationLog?.[0];
      if (!config?.showVerificationNotification || !latestVerificationLog) {
        message.success(
          t('stakeholderVerification.stakeholderDataReviewedSuccessfully'),
        );
        return;
      }

      notification.open(
        notificationConfig(latestVerificationLog, () =>
          navigate(
            stakeholderRoute(variables.stakeholderId)[
              variables.stakeholderType || 'PADDY_BUYER'
            ],
          ),
        ),
      );
    },
    onError: (error, variables) => {
      if (!config?.showVerificationNotification) {
        handleAxiosError(
          error,
          t('stakeholderVerification.failedToVerifyStakeholderData'),
        );
        return;
      }

      notification.error({
        message: t('stakeholderVerification.verificationFailed'),
        description: t('stakeholderVerification.weCouldntVerifyData'),
        btn: (
          <Button
            type="primary"
            onClick={() => mutation.mutate(variables)}
            icon={<SyncOutlined />}
          >
            Retry Verification
          </Button>
        ),
      });
    },
    onSettled: () => {
      message.destroy('verify-stakeholder');
    },
  });

  return mutation;
};

const stakeholderRoute = (id?: string) => ({
  PADDY_BUYER: '/stakeholders/paddy-buyer?paddyBuyerId=' + id,
  FARMER_GROUP_LEADER: '/stakeholders/farmer-group-leader?leaderId=' + id,
  MACHINE_OWNER: '/stakeholders/machine-owner?machineOwnerId=' + id,
});

const notificationConfig = (
  data?: DataVerificationLog,
  onClick?: () => void,
): ArgsProps => {
  const { dataVerifiedStatus } = { ...data };
  const message = dataVerifiedStatus
    ? t('stakeholderVerification.stakeholderDataVerified')
    : t('stakeholderVerification.stakeholderDataVerifiedWithIssues');
  const description = dataVerifiedStatus
    ? t('stakeholderVerification.allDataPassed')
    : t('stakeholderVerification.someDataDidNotPass');

  return {
    key: 'stakeholder-verification',
    message,
    description,
    type: dataVerifiedStatus ? 'success' : 'warning',
    btn: (
      <Button type="primary" size="small" onClick={onClick}>
        View Issues
      </Button>
    ),
  };
};

export default useMutateVerifyOpenAI;
